import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form, Button, Spinner } from 'react-bootstrap';

/**
 * QuestionErrorCodeFields - Horizontal layout component for QuestionCode and ErrorCode fields with inline Save button
 * 
 * @param {Object} props
 * @param {string} props.questionCode - Current QuestionCode value
 * @param {string} props.errorCode - Current ErrorCode value
 * @param {Function} props.onQuestionCodeChange - Callback when QuestionCode changes
 * @param {Function} props.onErrorCodeChange - Callback when ErrorCode changes
 * @param {Function} props.onSave - Callback when Save button is clicked
 * @param {boolean} props.disabled - Whether the fields are disabled
 * @param {boolean} props.saving - Whether save operation is in progress
 * @param {boolean} props.readOnly - Whether to show as read-only text labels instead of inputs
 */
const QuestionErrorCodeFields = ({
  questionCode,
  errorCode,
  onQuestionCodeChange,
  onErrorCodeChange,
  onSave,
  disabled,
  saving,
  readOnly
}) => {
  const MAX_LENGTH = 500;

  const handleQuestionCodeChange = (e) => {
    const value = e.target.value;
    onQuestionCodeChange(value);
  };

  const handleErrorCodeChange = (e) => {
    const value = e.target.value;
    onErrorCodeChange(value);
  };

  // If read-only mode, show as text labels
  if (readOnly) {
    return (
      <Row className="mb-3">
        <Col md={6}>
          <div>
            <strong>Question Code:</strong>{' '}
            <span className="text-muted">
              {questionCode || 'N/A'}
            </span>
          </div>
        </Col>
        
        <Col md={6}>
          <div>
            <strong>Error Code:</strong>{' '}
            <span className="text-muted">
              {errorCode || 'N/A'}
            </span>
          </div>
        </Col>
      </Row>
    );
  }

  // Editable mode with inputs and Save button
  return (
    <Row className="mb-3">
      <Col md={5}>
        <Form.Group>
          <Form.Label>Question Code</Form.Label>
          <Form.Control
            type="text"
            value={questionCode}
            onChange={handleQuestionCodeChange}
            disabled={disabled}
            maxLength={MAX_LENGTH}
            placeholder="Enter question or issue details"
          />
          <Form.Text className="text-muted">
            {(questionCode || '').length} / {MAX_LENGTH} characters
          </Form.Text>
        </Form.Group>
      </Col>
      
      <Col md={5}>
        <Form.Group>
          <Form.Label>Error Code</Form.Label>
          <Form.Control
            type="text"
            value={errorCode}
            onChange={handleErrorCodeChange}
            disabled={disabled}
            maxLength={MAX_LENGTH}
            placeholder="Enter error details"
          />
          <Form.Text className="text-muted">
            {(errorCode || '').length} / {MAX_LENGTH} characters
          </Form.Text>
        </Form.Group>
      </Col>
      
      <Col md={2} className="d-flex align-items-start">
        <Button 
          variant="primary" 
          onClick={onSave} 
          disabled={disabled || saving}
          className="w-100"
          style={{ marginTop: '32px' }}
        >
          {saving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Col>
    </Row>
  );
};

QuestionErrorCodeFields.propTypes = {
  questionCode: PropTypes.string,
  errorCode: PropTypes.string,
  onQuestionCodeChange: PropTypes.func.isRequired,
  onErrorCodeChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  saving: PropTypes.bool,
  readOnly: PropTypes.bool
};

QuestionErrorCodeFields.defaultProps = {
  questionCode: '',
  errorCode: '',
  disabled: false,
  saving: false,
  readOnly: false
};

export default QuestionErrorCodeFields;
